import { FC, useCallback } from 'react';
import { css } from '@emotion/react';
import { Col, Row } from 'reactstrap';

import { useForm, useFormState } from '@/utils/hooks';
import { CompanySettings } from '@/types/company-settings';
import config, {
  crew_member_username,
  crew_member_password,
  deactivate_vessel_access_days_after_sign_off,
  CrewAutomatedCredentialsType,
  crew_ticketing_cases_show_pdf_export,
  CrewTicketingCasesShowPdfExport,
  CrewWageGroupAutomationFrom,
  CrewWageGroupAutomationTo,
  crew_member_external_id_keys,
  crew_wage_group_automation_from,
  crew_wage_group_automation_to
} from 'views/companies/settings/crew-automated-credentials/config';
import { FieldsStateType } from '@/common/types/form';

import Category from 'views/companies/settings/Category';
import SubCategory from 'views/companies/settings/SubCategory';
import VisibleSetting from 'views/companies/settings/VisibleSetting';
import InputLabel from 'views/companies/settings/components/InputLabel';
import NumberInput from '@/library/components/form/inputs/NumberInput';
import Input from '@/library/components/form/inputs/Input';
import Select from '@/library/components/form/inputs/select';
import DoubleInputRow from '../components/DoubleInputRow';
import add from 'common/assets/svg/actions/add.svg';
import SvgRender from '@/library/components/general/SvgRender';
import { CATEGORY_IDS } from '../constants';
import { compactCrewExternalIdKeys, getOptionValue } from '../helpers';
import { useCategoryDraft } from '../useCategoryDraft';
import { formatOptionDisplay } from '../change-tracking';
import { useCompanySettingsDraft } from '../draft-context';

type CrewAutomatedCredentialsProps = {
  companySettings: CompanySettings | undefined;
};

const CATEGORY_ID = CATEGORY_IDS.crew;
const CATEGORY_TITLE = 'Crew';

const TICKETING_OPTIONS: Array<{ label: string; value: 0 | 1 }> = [
  { label: 'Yes/true', value: 1 },
  { label: 'No/False', value: 0 }
];

const WAGE_FROM_OPTIONS: Array<{
  label: string;
  value: 'sign_on_date' | 'contract_from_date';
}> = [
  { label: 'Sign on', value: 'sign_on_date' },
  { label: 'Contract starts', value: 'contract_from_date' }
];

const WAGE_TO_OPTIONS: Array<{
  label: string;
  value: 'sign_off_date' | 'contract_to_date' | 'arrived_home_date';
}> = [
  { label: 'Sign off', value: 'sign_off_date' },
  { label: 'Contract ends', value: 'contract_to_date' },
  { label: 'Arrived home', value: 'arrived_home_date' }
];

const LABELS = {
  [crew_member_username]: 'Username Pattern',
  [crew_member_password]: 'Password Pattern',
  [deactivate_vessel_access_days_after_sign_off]: 'Deactivate Vessel Access',
  [crew_ticketing_cases_show_pdf_export]: 'Crew Ticketing Show PDF Export',
  [crew_member_external_id_keys]: 'Crew Member External ID Keys',
  [crew_wage_group_automation_from]: 'Crew Wage Group Automation From',
  [crew_wage_group_automation_to]: 'Crew Wage Group Automation To'
};

const CrewAutomatedCredentials: FC<CrewAutomatedCredentialsProps> = ({ companySettings }) => {
  const { formState, loadValues, collectValues } = useForm(config);
  const { changeField, selectField, subStates, addSubform, removeSubform, ...rest } =
    useFormState(formState);
  const fields: FieldsStateType<CrewAutomatedCredentialsType> = rest.fields;
  const { isSettingVisible } = useCompanySettingsDraft();

  const externalIds = subStates('crew_member_external_id_keys');

  const initializeForm = useCallback(() => {
    if (!companySettings) return;

    loadValues({
      [crew_member_username]: companySettings.crew_member_username,
      [crew_member_password]: companySettings.crew_member_password,
      [deactivate_vessel_access_days_after_sign_off]:
        companySettings.deactivate_vessel_access_days_after_sign_off,
      [crew_ticketing_cases_show_pdf_export]: companySettings[crew_ticketing_cases_show_pdf_export]
        ? { label: 'Yes/true', value: 1 }
        : companySettings[crew_ticketing_cases_show_pdf_export] === 0
          ? { label: 'No/False', value: 0 }
          : null,
      [crew_member_external_id_keys]: companySettings[crew_member_external_id_keys],
      [crew_wage_group_automation_from]: companySettings[crew_wage_group_automation_from],
      [crew_wage_group_automation_to]: companySettings[crew_wage_group_automation_to]
    });
  }, [companySettings, loadValues]);

  useCategoryDraft({
    categoryId: CATEGORY_ID,
    categoryTitle: CATEGORY_TITLE,
    companySettings,
    initializeForm,
    collectValues: (validate = true) =>
      collectValues(validate) as CrewAutomatedCredentialsType | null,
    toPayload: values => [
      { key: crew_member_username, value: values[crew_member_username] },
      { key: crew_member_password, value: values[crew_member_password] },
      {
        key: deactivate_vessel_access_days_after_sign_off,
        value: values[deactivate_vessel_access_days_after_sign_off]
      },
      {
        key: crew_ticketing_cases_show_pdf_export,
        value: getOptionValue(values[crew_ticketing_cases_show_pdf_export])
      },
      {
        key: crew_member_external_id_keys,
        value: compactCrewExternalIdKeys(values[crew_member_external_id_keys])
      },
      {
        key: crew_wage_group_automation_from,
        value: getOptionValue(values[crew_wage_group_automation_from])
      },
      {
        key: crew_wage_group_automation_to,
        value: getOptionValue(values[crew_wage_group_automation_to])
      }
    ],
    labels: LABELS,
    formatValue: (key, value) => {
      if (key === crew_ticketing_cases_show_pdf_export) {
        return formatOptionDisplay(value, TICKETING_OPTIONS);
      }
      if (key === crew_wage_group_automation_from) {
        return formatOptionDisplay(value, WAGE_FROM_OPTIONS);
      }
      if (key === crew_wage_group_automation_to) {
        return formatOptionDisplay(value, WAGE_TO_OPTIONS);
      }
      return formatOptionDisplay(value);
    },
    watch: [fields, externalIds]
  });

  return (
    <Category id={CATEGORY_ID} title={CATEGORY_TITLE}>
      <SubCategory
        title="Login credentials"
        hidden={
          !isSettingVisible(CATEGORY_ID, crew_member_username) &&
          !isSettingVisible(CATEGORY_ID, crew_member_password)
        }
        className="border-bottom border-gray-200"
      >
        <Row className="d-flex align-items-center gx-1">
          <Col xs={3}>
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={crew_member_username}>
              <Input
                className="mb-0"
                innerClassName="h-24"
                label={<InputLabel title="USERNAME PATTERN" apiKey={crew_member_username} />}
                placeholder="Add pattern"
                onChange={changeField(crew_member_username)}
                {...fields[crew_member_username]}
              />
            </VisibleSetting>
          </Col>
          <Col xs={3} className="d-flex flex-column justify-content-between h-100p">
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={crew_member_password}>
              <Input
                className="mb-0"
                innerClassName="h-24"
                label={<InputLabel title="PASSWORD PATTERN" apiKey={crew_member_password} />}
                placeholder="Add pattern"
                onChange={changeField(crew_member_password)}
                {...fields[crew_member_password]}
              />
            </VisibleSetting>
          </Col>
        </Row>
      </SubCategory>

      <SubCategory
        title="Sign-off automation"
        hidden={
          !isSettingVisible(CATEGORY_ID, deactivate_vessel_access_days_after_sign_off) &&
          !isSettingVisible(CATEGORY_ID, crew_wage_group_automation_from) &&
          !isSettingVisible(CATEGORY_ID, crew_wage_group_automation_to)
        }
        className="border-bottom border-gray-200"
      >
        <Row className="d-flex align-items-center gx-1">
          <Col
            xs={3}
            css={css`
              .form-field {
                width: 8.5rem;
              }
            `}
          >
            <VisibleSetting
              categoryId={CATEGORY_ID}
              settingKey={deactivate_vessel_access_days_after_sign_off}
            >
              <InputLabel
                title="DEACTIVATE VESSEL ACCESS"
                apiKey={deactivate_vessel_access_days_after_sign_off}
              />
              <div className="d-flex align-items-center cmt-4">
                <NumberInput
                  className="mb-0"
                  innerClassName="h-24"
                  placeholder="Add no."
                  decimalScale={0}
                  onChange={changeField(deactivate_vessel_access_days_after_sign_off)}
                  {...fields[deactivate_vessel_access_days_after_sign_off]}
                />
                <div className="text-primary fs-12 fw-medium ms-1">days after sign off</div>
              </div>
            </VisibleSetting>
          </Col>
        </Row>
        <Row className="d-flex align-items-start gx-1 mt-1">
          <Col xs={3}>
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={crew_wage_group_automation_from}>
              <InputLabel
                title="CREW WAGE GROUP AUTOMATION FROM"
                apiKey={crew_wage_group_automation_from}
              />
              <Select
                className="cmy-2"
                options={WAGE_FROM_OPTIONS}
                onChange={(option: CrewWageGroupAutomationFrom) => {
                  selectField(crew_wage_group_automation_from)(option);
                }}
                {...fields[crew_wage_group_automation_from]}
              />
            </VisibleSetting>
          </Col>
          <Col xs={3}>
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={crew_wage_group_automation_to}>
              <InputLabel
                title="CREW WAGE GROUP AUTOMATION TO"
                apiKey={crew_wage_group_automation_to}
              />
              <Select
                className="cmy-2"
                options={WAGE_TO_OPTIONS}
                onChange={(option: CrewWageGroupAutomationTo) => {
                  selectField(crew_wage_group_automation_to)(option);
                }}
                {...fields[crew_wage_group_automation_to]}
              />
            </VisibleSetting>
          </Col>
        </Row>
      </SubCategory>

      <SubCategory
        title="Profile & identity"
        hidden={!isSettingVisible(CATEGORY_ID, crew_member_external_id_keys)}
        className="border-bottom border-gray-200"
      >
        <VisibleSetting categoryId={CATEGORY_ID} settingKey={crew_member_external_id_keys}>
          <Row className="cmt-12">
            <Col xs={5}>
              <InputLabel
                title="CREW MEMBER EXTERNAL ID KEYS"
                apiKey={crew_member_external_id_keys}
                className="mb-0"
              />
            </Col>
          </Row>
          <Row className="d-flex align-items-start">
            <Col xs={6}>
              {externalIds.map((external, index) => (
                <DoubleInputRow
                  key={index}
                  state={external}
                  index={index}
                  onRemove={removeSubform}
                />
              ))}
            </Col>
          </Row>
          <Row className="cmt-12">
            <Col xs={3} className="d-flex justify-content-end">
              <div
                onClick={() => addSubform(crew_member_external_id_keys)}
                className="fs-12 text-aqua fw-medium d-flex align-items-center cursor-pointer"
              >
                <SvgRender src={add} className="cme-6" style={{ width: 11, height: 11 }} />
                <div className="lh-16">Add more</div>
              </div>
            </Col>
          </Row>
        </VisibleSetting>
      </SubCategory>

      <SubCategory
        title="Ticketing"
        hidden={!isSettingVisible(CATEGORY_ID, crew_ticketing_cases_show_pdf_export)}
        className="cpb-2 cmb-2"
      >
        <VisibleSetting categoryId={CATEGORY_ID} settingKey={crew_ticketing_cases_show_pdf_export}>
          <Row className="d-flex align-items-center">
            <Col xs={12} className="cmb-4">
              <InputLabel
                title="CREW TICKETING SHOW PDF EXPORT"
                apiKey={crew_ticketing_cases_show_pdf_export}
              />
            </Col>
          </Row>
          <Row className="d-flex align-items-center">
            <Col xs={3}>
              <Select
                className="mb-0"
                options={TICKETING_OPTIONS}
                onChange={(option: CrewTicketingCasesShowPdfExport) => {
                  selectField(crew_ticketing_cases_show_pdf_export)(option);
                }}
                {...fields[crew_ticketing_cases_show_pdf_export]}
              />
            </Col>
          </Row>
        </VisibleSetting>
      </SubCategory>
    </Category>
  );
};

export default CrewAutomatedCredentials;
